/* eslint-disable no-nested-ternary */
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { Image as SiteImage, RichText, SiteLink, ArticleAccordion } from 'components/shared';
import VideoPlayer from 'components/shared/video-player';
import { getLinkHref } from 'lib/utils/helpers';
import Sitelink from 'components/shared/sitelink';
import Image from 'components/shared/image';
import { trackEvents, trackInWindow } from 'hooks/tracking/track';
import { useUI } from 'hooks';
import classNames from 'classnames';
import { animated } from 'react-spring';
import { addNewLines } from './articles/richTextOptions';
import validateRequired from './validate-required';
import withPropValidation from './with-props-validation.tsx';
import Button from 'components/shared/button';

const richTextOptions = richText => {
  const hyperlink = richText?.links?.entries?.hyperlink || [];
  const getLinkedEntry = linkId => hyperlink.find(l => l?.sys?.id === linkId);

  return {
    renderNode: {
      [BLOCKS.HEADING_1]: (_, children) => <h1 className="text-heading-1">{children}</h1>,
      [BLOCKS.HEADING_2]: (_, children) => <h2 className="text-heading-2">{children}</h2>,
      [BLOCKS.HEADING_3]: (_, children) => <p className="text-heading-3">{children}</p>,
      [BLOCKS.HEADING_4]: (_, children) => <p className="text-heading-4">{children}</p>,
      [BLOCKS.HEADING_5]: (_, children) => <p className="text-heading-5">{children}</p>,
      [BLOCKS.HEADING_6]: (_, children) => <p className="text-heading-6">{children}</p>,
      [BLOCKS.UL_LIST]: (_, children) => (
        <ul className="mb-[25px] list-disc pl-[25px]">{children}</ul>
      ),
      [BLOCKS.LIST_ITEM]: (_, children) => {
        const addBottomMargin = c =>
          c.type === 'p'
            ? { ...c, props: { ...c.props, className: `${c.props.className} mb-[4px]` } }
            : c;
        const content = children.map(addBottomMargin);

        return <li className="pl-[7px]">{content}</li>;
      },
      [BLOCKS.PARAGRAPH]: (_, children) => {
        const isEmpty = children.length === 1 && children[0] === '';
        if (isEmpty) {
          return <br />;
        }

        return <p>{addNewLines(children)}</p>;
      },
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        const {
          data: {
            target: {
              sys: { id },
            },
          },
        } = node;

        const link = getLinkedEntry(id);

        if (!link) {
          return children;
        }

        return (
          <SiteLink href={getLinkHref(link)} className="underline">
            {children}
          </SiteLink>
        );
      },
    },
  };
};

const linkClassNames = {
  'Text (White)': 'button underline px-6 bg-transparent pointer-events-none',
  'Button (White)': 'button w-full bg-white px-6 text-blue lg:px-12 pointer-events-none',
  'Button (Blue)': 'button w-full bg-blue px-6 text-white lg:px-12 pointer-events-none',
};

const Section = ({
  data: {
    article,
    article: {
      article: articleText,
      title,
      vimeoUrl: desktopVimeoUrl,
      mobileVimeoUrl,
      image: desktopImage,
      mobileImage,
    },
    showImageOnLeft,
    linksCollection: { items: linkItems },
    paddingTop,
    paddingBottom,
    buttonsCollection: { items: buttons },
    sectionId,
  },
}) => {
  const { isDesktop } = useUI();
  const articleHasSubmenus = article.submenusCollection.items.filter(Boolean).length > 0;
  const links = linkItems.filter(Boolean);

  const vimeoUrl = isDesktop ? desktopVimeoUrl : mobileVimeoUrl || desktopVimeoUrl;
  const image = isDesktop ? desktopImage : mobileImage || desktopImage;

  const ArticleButton = ({
    button: {
      __typename,
      name,
      image: buttonImage,
      link: { link: buttonLink, externalLink, query, text: buttonText },
      style,
    },
  }) => {
    const handleClick = () => {
      const lastWord = name.trim().split(' ').reverse()[0];

      if (['iOS', 'Android'].includes(lastWord)) {
        trackInWindow({
          event: trackEvents.downloadApp,
          operating_system: lastWord,
          page_path: window.location.pathname,
        });
      }
    };

    const renderComponentImageLink = () => (
      <Sitelink href={getLinkHref(buttonLink)} external={externalLink} onClick={handleClick}>
        <Image
          src={{
            ...buttonImage,
            width: (buttonImage?.width / buttonImage?.height) * 50,
            height: 50,
          }}
          layout="intrinsic"
        />
      </Sitelink>
    );

    const renderComponentButton = () => (
      <Button
        link={{ link: buttonLink, externalLink, query, text: buttonText }}
        type={style}
        className="block w-full lg:w-fit"
        query={query}
      />
    );

    return __typename === 'ComponentImageLink'
      ? renderComponentImageLink()
      : renderComponentButton();
  };

  return (
    <div
      id={sectionId}
      className={classNames(
        'flex h-full w-full flex-col lg:flex-row',
        {
          'pt-14': paddingTop,
          'pb-14': paddingBottom,
        },
        showImageOnLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'
      )}
    >
      {/* Media */}
       {(image || vimeoUrl) && (
        <div className={classNames('w-full lg:aspect-[0.83] aspect-1 lg:w-1/2', 'shrink-0 lg:basis-1/2')}>
          <div className="relative aspect-1 h-full w-full lg:aspect-auto">
            {vimeoUrl ? (
              <VideoPlayer
                className="absolute inset-0 h-full w-full"
                videoClassName="absolute inset-0 w-full h-full"
                videoURL={vimeoUrl}
              />
            ) : (
              <SiteImage
                src={image}
                sizes="(min-width: 1024px) 40vw, 100vw"
                className="absolute inset-0 h-full w-full"
              />
            )}
          </div>
        </div>
      )}

      {/* Text */}
      <div className="w-full p-12 text-[14px] lg:flex lg:w-1/2 lg:basis-1/2 lg:flex-col lg:gap-3 lg:p-24">
        {/* Submenus */}
        {articleHasSubmenus ? (
          <div className="min-h-[40rem]">
            <ArticleAccordion
              key={article.title}
              article={article}
              titleClass="font-nimbus-sans-extd-d font-bold uppercase text-[25px] lg:text-[35px] leading-[0.9] mb-[30px] text-left"
              submenuTitleClass="text-[14px] font-nimbus-sans-extd-d font-bold uppercase text-left"
              borders
            />
          </div>
        ) : (
          <div>
            {/* No submenus */}
            {title && (
              <h3 className="mb-8 font-nimbus-sans-extd-d text-[35px] font-bold uppercase leading-[0.9]">
                {title.split('<br>').map(l => (
                  <span className="block">{l}</span>
                ))}
              </h3>
            )}
            {articleText && <RichText data={articleText} options={richTextOptions(articleText)} />}
            {Boolean(buttons.length) && (
              <div className="flex flex-wrap items-center gap-x-8 gap-y-6">
                {buttons.map(button => (
                  <ArticleButton key={button.name} button={button} />
                ))}
              </div>
            )}
            {Boolean(links.length) && (
              <div className="font-nimbus-sans-extd-d text-[15px] font-bold uppercase">
                {links.map(({ link, externalLink, text }) => (
                  <SiteLink key={text} href={getLinkHref(link)} external={externalLink}>
                    {text}
                  </SiteLink>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default withPropValidation(Section, ({ data: { name, article } }) =>
  validateRequired({ Name: name, Article: article })
);
